import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { CircleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import {
  ACCOUNT_DELETION_REASONS,
  type AccountDeletionInput,
  type AccountDeletionReason,
  type User,
} from '@/Domain/api/User/dto/User.dto';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { PasswordInput } from '@/Domain/components/Shared/Input/PasswordInput';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import { useLogout } from '@/Domain/hooks/Auth/useLogout';
import { cn } from '@/Domain/utils/cn';

type Props = {
  user: User;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type ApiErrorBody = { errors?: Record<string, { message: string; code: string }[]> };
type DeletionError = { key: string; field?: 'email' | 'password'; defaultValue?: string };

const BAD_REQUEST_STATUS = 400;
const CONFLICT_STATUS = 409;
const TOO_MANY_REQUESTS_STATUS = 429;
const ERROR_ID = 'delete-account-error';
const REASON_DETAILS_MAX_LENGTH = 500;

const toDeletionError = (error: AxiosError<ApiErrorBody>): DeletionError => {
  const status = error.response?.status;
  const errors = error.response?.data?.errors ?? {};
  if (status === BAD_REQUEST_STATUS && errors.current_password) {
    return { key: 'error_wrong_password', field: 'password' };
  }
  if (status === BAD_REQUEST_STATUS && errors.confirmation_email) {
    return { key: 'error_email_mismatch', field: 'email' };
  }
  const blocker = errors.root?.[0];
  if (status === CONFLICT_STATUS && blocker) {
    return { key: `blocked_${blocker.code}`, defaultValue: blocker.message };
  }
  if (status === TOO_MANY_REQUESTS_STATUS) return { key: 'error_throttled' };
  return { key: 'error_generic' };
};

export default function DeleteAccountDialog({ user, open, onOpenChange }: Props): JSX.Element {
  const { t } = useTranslation(['user_profile', 'common']);
  const { handleLogout } = useLogout();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState<AccountDeletionReason | ''>('');
  const [reasonDetails, setReasonDetails] = useState('');
  const deletion = useMutation<void, AxiosError<ApiErrorBody>, AccountDeletionInput>({
    mutationKey: [QueryKey.user.deleteAccount],
    onSuccess: () => handleLogout(),
  });
  const requiresPassword = user.has_password !== false;
  // Stays busy after success, while logging out navigates away.
  const busy = deletion.isPending || deletion.isSuccess;
  const error = deletion.error ? toDeletionError(deletion.error) : null;
  const errorField = error?.field === 'password' && !requiresPassword ? undefined : error?.field;
  const errorMessage = error && (
    <p
      id={ERROR_ID}
      role="alert"
      className={cn(
        'text-sm text-destructive animate-in fade-in slide-in-from-top-1 duration-200',
        !errorField && 'rounded-lg border border-destructive-muted bg-destructive-muted p-3',
      )}
      data-testid="delete-account-error"
    >
      {t(`delete_account.dialog.${error.key}`, { defaultValue: error.defaultValue })}
    </p>
  );
  const canSubmit =
    email.trim().toLowerCase() === user.email.toLowerCase() &&
    (!requiresPassword || password.length > 0) &&
    !busy;

  useEffect(() => {
    setEmail('');
    setPassword('');
    setReason('');
    setReasonDetails('');
    deletion.reset();
    // Reset on open/close only; `reset` is stable enough for that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = () => {
    const details = reasonDetails.trim();
    deletion.mutate({
      confirmation_email: email.trim(),
      ...(requiresPassword ? { current_password: password } : {}),
      ...(reason ? { reason } : {}),
      ...(details ? { reason_details: details } : {}),
    });
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next);
      }}
    >
      <AlertDialogContent
        className="max-h-[90dvh] overflow-y-auto"
        closeButtonProps={{ disabled: busy }}
        data-testid="delete-account-dialog"
      >
        <AlertDialogHeader className="space-y-3">
          <AlertDialogTitle>
            <CircleAlert className="size-4 bg-destructive-muted rounded-lg text-destructive p-1.5 box-content" />
            {t('delete_account.dialog.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('delete_account.dialog.intro')}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="delete-account-reason">{t('delete_account.dialog.reason_label')}</Label>
            <Select
              value={reason}
              disabled={busy}
              onValueChange={(value) => setReason(value as AccountDeletionReason)}
            >
              <SelectTrigger
                id="delete-account-reason"
                size="md"
                className="w-full"
                data-testid="delete-account-reason-trigger"
              >
                <SelectValue placeholder={t('delete_account.dialog.reason_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {ACCOUNT_DELETION_REASONS.map((code) => (
                  <SelectItem
                    key={code}
                    value={code}
                    data-testid={`delete-account-reason-option-${code}`}
                  >
                    {t(`delete_account.dialog.reason_options.${code}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="delete-account-reason-details">
              {t('delete_account.dialog.reason_details_label')}
            </Label>
            <AutosizeTextarea
              id="delete-account-reason-details"
              value={reasonDetails}
              placeholder={t('delete_account.dialog.reason_details_placeholder')}
              maxLength={REASON_DETAILS_MAX_LENGTH}
              minHeight={72}
              maxHeight={160}
              className="px-4"
              disabled={busy}
              onChange={(event) => setReasonDetails(event.target.value)}
              aria-describedby="delete-account-reason-details-note"
              data-testid="delete-account-reason-details-input"
            />
            <p
              id="delete-account-reason-details-note"
              className="text-xs text-muted-foreground"
              data-testid="delete-account-reason-details-note"
            >
              {t('delete_account.dialog.reason_details_note')}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="delete-account-email" className="leading-snug">
              <Trans
                i18nKey="delete_account.dialog.email_label"
                t={t}
                values={{ email: user.email }}
                components={{ bold: <span className="font-semibold break-words" /> }}
              />
            </Label>
            <Input
              id="delete-account-email"
              value={email}
              autoComplete="off"
              disabled={busy}
              onChange={(event) => setEmail(event.target.value)}
              error={errorField === 'email'}
              aria-invalid={errorField === 'email' || undefined}
              aria-describedby={errorField === 'email' ? ERROR_ID : undefined}
              data-testid="delete-account-email-input"
            />
            {errorField === 'email' && errorMessage}
          </div>
          {requiresPassword && (
            <div className="space-y-2">
              <Label htmlFor="delete-account-password">
                {t('delete_account.dialog.password_label')}
              </Label>
              <PasswordInput
                id="delete-account-password"
                value={password}
                autoComplete="current-password"
                disabled={busy}
                onChange={(event) => setPassword(event.target.value)}
                error={errorField === 'password'}
                aria-invalid={errorField === 'password' || undefined}
                aria-describedby={errorField === 'password' ? ERROR_ID : undefined}
                data-testid="delete-account-password-input"
              />
              {errorField === 'password' && errorMessage}
            </div>
          )}
          {error && !errorField && errorMessage}
        </div>
        <AlertDialogFooter className="gap-2 sm:space-x-0">
          <AlertDialogCancel asChild disabled={busy}>
            <Button variant="secondary" disabled={busy} data-testid="delete-account-cancel-button">
              {t('cancel', { ns: 'common' })}
            </Button>
          </AlertDialogCancel>
          <Button
            variant="danger"
            disabled={!canSubmit}
            onClick={submit}
            aria-busy={busy || undefined}
            className={cn(
              busy &&
                'disabled:!border-destructive disabled:!bg-destructive disabled:!text-destructive-foreground',
            )}
            data-testid="delete-account-confirm-button"
          >
            {busy && <Spinner className="text-white" aria-hidden="true" />}
            {t('delete_account.button')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
